import React from 'react';
import { Tag } from 'antd';
import {
  createColumnText,
  createColumnDate,
  createColumnCenter,
  renderWithDetail,
  widthOptions,
} from '../table/function/createColumn';
import TableComponent from '../table';
import { color } from '../../resources';

// remainingDays และ warrantyStatus = 'EXPIRED' คำนวณจาก expireDate ที่ procedure อยู่แล้ว
// เกิน 36000 วัน = ประกันตลอดชีพ (เกณฑ์เดียวกับหน้ารายละเอียดเคลม)
const isWarrantyExpired = (record) =>
  record?.warrantyStatus === 'EXPIRED' || Number(record?.remainingDays) < 0;

const remainingWarrantyText = (record) => {
  if (isWarrantyExpired(record)) return 'หมดอายุ';
  const days = Number(record?.remainingDays);
  if (!Number.isFinite(days)) return '-';
  if (days > 36000) return 'ตลอดชีวิต';
  return `อีก ${days} วัน`;
};

// สีแถวของตารางหลักมาจากจำนวนเคลมของใบนั้น ไม่ใช่สถานะหมดอายุอีกต่อไป
export const getClaimCountRowClass = (record) => {
  const count = Number(record?.claimCount) || 0;
  if (count >= 5) return 'claim-count-row-danger';
  if (count >= 3) return 'claim-count-row-warning';
  return '';
};

export const customerHistoryColumns = [
  createColumnText(
    'เลขที่รับประกัน',
    'warrantyCode',
    widthOptions(160, {
      render: (text, record) =>
        renderWithDetail(text, `วันที่ลงทะเบียน: ${record.createDate || '-'}`),
    }),
  ),
  createColumnText('รหัสสินค้า', 'itemCode', widthOptions(140)),
  createColumnText('ชื่อสินค้า', 'productName', widthOptions(180)),
  createColumnText(
    'รุ่น',
    'mobileModelName',
    widthOptions(150, {
      render: (text, record) => renderWithDetail(text, record.mobileBrandName || '-'),
    }),
  ),
  createColumnDate(
    'วันหมดอายุ',
    'expireDate',
    widthOptions(140, {
      render: (text, record) => renderWithDetail(text, remainingWarrantyText(record)),
    }),
  ),
  createColumnCenter(
    'จำนวนเคลม',
    'claimCount',
    widthOptions(110, {
      render: (value) => value || 0,
    }),
  ),
];

const nestedColumnOptions = (width, other = {}) => ({
  noSearch: true,
  noSort: true,
  others: { width, ...other },
});

const claimNestedColumns = [
  createColumnText(
    'เลขที่เคลม',
    'claimCode',
    nestedColumnOptions(180, {
      render: (text, claim) => renderWithDetail(text, `วันที่เคลม: ${claim.claimDate || '-'}`),
    }),
  ),
  createColumnText('สาเหตุ', 'breakdownName', nestedColumnOptions(180)),
  createColumnText('สถานะ', 'claimStatusName', nestedColumnOptions(140)),
  createColumnText('เลขพัสดุ', 'trackingNumber', nestedColumnOptions(160)),
  {
    title: 'ไม่ตัดประกัน',
    dataIndex: 'noDeductWarranty',
    align: 'center',
    width: 110,
    skipExport: true,
    render: (value) => (value === 'Y' ? <Tag color={color.orangeTheme}>ไม่ตัดประกัน</Tag> : null),
  },
];

// ตารางย่อยของเคลมในแต่ละใบประกัน ไม่มีเคลม = dataSource ว่าง แสดง empty state ของตารางเอง
export const CustomerWarrantyClaimsTable = ({ record }) => (
  <TableComponent rowKey="claimId" columns={claimNestedColumns} dataSource={record?.claims || []} />
);

// Excel: แถวละ (ประกัน, เคลม) ประกันที่ไม่มีเคลมได้หนึ่งแถวโดยช่องเคลมว่าง
export const customerHistoryExportHeaders = [
  { dataIndex: 'warrantyCode', title: 'เลขที่รับประกัน' },
  { dataIndex: 'createDate', title: 'วันที่ลงทะเบียน' },
  { dataIndex: 'itemCode', title: 'รหัสสินค้า' },
  { dataIndex: 'productName', title: 'ชื่อสินค้า' },
  { dataIndex: 'mobileModelName', title: 'รุ่น' },
  { dataIndex: 'mobileBrandName', title: 'แบรนด์' },
  { dataIndex: 'expireDate', title: 'วันหมดอายุ' },
  { dataIndex: 'remainingText', title: 'วันคงเหลือ' },
  { dataIndex: 'claimCount', title: 'จำนวนเคลม' },
  { dataIndex: 'claimCode', title: 'เลขที่เคลม' },
  { dataIndex: 'claimDate', title: 'วันที่เคลม' },
  { dataIndex: 'breakdownName', title: 'สาเหตุการเคลม' },
  { dataIndex: 'claimStatusName', title: 'สถานะเคลม' },
  { dataIndex: 'trackingNumber', title: 'เลขพัสดุ' },
  { dataIndex: 'noDeductText', title: 'ไม่ตัดประกัน' },
];

const emptyClaim = {
  claimCode: '',
  claimDate: '',
  breakdownName: '',
  claimStatusName: '',
  trackingNumber: '',
  noDeductWarranty: '',
};

// แถว Excel ใช้ค่าเดียวกับที่แสดงบนตาราง (วันคงเหลือ / ไม่ตัดประกันแสดงรายเคลม)
export const toCustomerHistoryExportRows = (rows = []) =>
  rows.flatMap(({ claims, ...warranty }) => {
    const base = {
      ...warranty,
      remainingText: remainingWarrantyText(warranty),
      claimCount: warranty.claimCount || 0,
    };
    const claimList = claims?.length ? claims : [emptyClaim];
    return claimList.map((claim) => ({
      ...base,
      claimCode: claim.claimCode,
      claimDate: claim.claimDate,
      breakdownName: claim.breakdownName,
      claimStatusName: claim.claimStatusName,
      trackingNumber: claim.trackingNumber,
      noDeductText: claim.noDeductWarranty === 'Y' ? 'ไม่ตัดประกัน' : '',
    }));
  });
